import React from 'react';
import './BookingsManagement.css';

/**
 * Pagination Component - Renders pagination controls for the bookings table
 * @param {Object} props - Component props
 * @param {Object} props.pagination - Pagination state object
 * @param {Function} props.onPageChange - Function to handle page changes
 * @returns {JSX.Element} - Rendered component
 */
function Pagination({ pagination, onPageChange }) {
  // Don't render pagination if there are no items
  if (!pagination.totalCount) {
    return null;
  }

  // Calculate actual total pages based on total count and limit
  const actualTotalPages = Math.ceil(pagination.totalCount / pagination.limit);

  // Generate page numbers to display
  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (actualTotalPages <= maxVisiblePages) {
      // If total pages is less than max visible, show all pages
      for (let i = 1; i <= actualTotalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate start and end of visible pages
      let startPage = Math.max(2, pagination.currentPage - 1);
      let endPage = Math.min(actualTotalPages - 1, pagination.currentPage + 1);
      
      // Adjust if we're near the beginning or end
      if (pagination.currentPage <= 2) {
        endPage = 4;
      } else if (pagination.currentPage >= actualTotalPages - 1) {
        startPage = actualTotalPages - 3;
      }
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (endPage < actualTotalPages - 1) {
        pageNumbers.push('ellipsis-end');
      }
      
      // Always show last page
      pageNumbers.push(actualTotalPages);
    }
    
    return pageNumbers;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div className="bookings-management__pagination-container">
      <div className="bookings-management__pagination">
        <button
          className="bookings-management__pagination-btn"
          disabled={pagination.currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, pagination.currentPage - 1))}
        >
          &lt;
        </button>
        
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis-start' || page === 'ellipsis-end') {
            return (
              <span key={`ellipsis-${index}`} className="bookings-management__pagination-ellipsis">
                ...
              </span>
            );
          }
          
          return (
            <button
              key={`page-${page}`}
              className={`bookings-management__pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>
          );
        })}
        
        <button
          className="bookings-management__pagination-btn"
          disabled={pagination.currentPage >= actualTotalPages}
          onClick={() => onPageChange(Math.min(actualTotalPages, pagination.currentPage + 1))}
        >
          &gt;
        </button>
      </div>
      
      <div className="bookings-management__pagination-info">
        Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} of {pagination.totalCount} bookings
      </div>
    </div>
  );
}

export default Pagination; 